package top.wcpe.allincore.dsl.compiler.semantic

import top.wcpe.allincore.dsl.compiler.api.ProjectTypeTable
import top.wcpe.allincore.dsl.compiler.ast.TypeAst
import top.wcpe.allincore.dsl.compiler.type.IrType

/**
 * M10 Stage 1：拆分自原 TypeConstraintChecker。
 *
 * 对应原 TypeConstraintChecker 的 dataTypeNames / stateFieldTypes / declaredVarTypes /
 * inferredVarTypes / currentFunParams / currentForLoopVars 等字段，与 reset /
 * resetForNewFun / withScopedDeclaredVars / withForLoopVar / currentVarEnv 等方法。
 *
 * 多个 checker 共享的可变上下文容器：在 walk 过程中持续维护当前作用域内的
 * data 类型名集合、state 字段类型表、局部 var 类型表与 fun 形参 / for 迭代变量
 * 集合，供 LocalVarTypeChecker / StateFieldChecker / NullSafetyChecker /
 * ControlFlowTypeChecker 协作使用。
 *
 * 不做任何诊断，仅提供"状态登记 + 作用域切换"原语；所有诊断逻辑由 4 个具体
 * checker 持有。
 */
internal class VarTypeEnv {

    /** M6-a：当前 screen 已声明的 data 类型名集合；这些名字应被视为合法已识别类型，避免 E621 误报。 */
    val dataTypeNames: MutableSet<String> = mutableSetOf()

    /** state 字段名 → 显式类型标注；仅含有类型标注的字段。 */
    val stateFieldTypes: MutableMap<String, TypeAst> = mutableMapOf()

    /**
     * M9.5 Stage 4 / E661：state 字段名 → 解析后的 IrType（含 nullable 包装）。
     *
     * 与 [stateFieldTypes] 配对维护——前者保留原始 TypeAst 用于空赋值检查
     * 的可空性判定，本表则把 TypeAst 一次性解析为 [IrType]，供 state 字段二次
     * 赋值时做类型一致性比较。命中未识别命名类型时不进表，让 E621 在
     * StateTypeChecker 路径处理；本表只对**已能解析**的字段登记。
     */
    val stateFieldIrTypes: MutableMap<String, IrType> = mutableMapOf()

    /**
     * M8 阶段 D：当前作用域内**已显式标注类型**的 var 名 → 解析后的 IrType。
     *
     * 仅在 walk 进入 fun / action 体之前 reset 一次，让每个 fun 的局部 var 各自独立。
     * 后续 AssignStmt 在表中查到对应名字时会检查 rhs 类型一致性。
     *
     * 不标注类型的 var 不进入此表——保持向后兼容（M5-c 起一直如此）。
     */
    val declaredVarTypes: MutableMap<String, IrType> = mutableMapOf()

    /**
     * M9 Stage 6 / E662：当前作用域内**未显式标注**的 var 名 → 初值推断 IrType。
     *
     * 与 [declaredVarTypes] 互斥——两表各自独立登记同一名字。后续 AssignStmt
     * 命中此表时，比较 actual vs 之前推断类型，不容则报 E662（INFO 级，非 E620）。
     *
     * 设计要点：
     * - rhs 推断为 AnyT 不登记，避免对绝大多数动态值产生假阳性
     * - 嵌套 fun 边界由 [withScopedDeclaredVars] 一并隔离两表
     */
    val inferredVarTypes: MutableMap<String, IrType> = mutableMapOf()

    /**
     * M9 Stage 6 / E660：当前正在 walk 的 fun 形参名集合。
     *
     * 仅在聚合器入口对每个 fun 重置。命中 LocalLValue 时报 E660（INFO 级）。
     */
    val currentFunParams: MutableSet<String> = mutableSetOf()

    /**
     * M9 Stage 6 / E670：当前嵌套 for 循环的迭代变量名集合（栈式累加）。
     *
     * 进入 ForInStmt body 前压入名字，退出后弹出（用 try / finally 保证嵌套
     * 循环退栈不互相污染）。命中 LocalLValue 时报 E670。
     */
    val currentForLoopVars: MutableSet<String> = mutableSetOf()

    /**
     * M10 Stage 4：当前 screen 内同文件 data 类的字段名集合（类名 → 字段名集合）。
     *
     * 由 [TypeConstraintChecker.check] 入口扫描 [top.wcpe.allincore.dsl.compiler.ast.dataClassDecls]
     * 一次性填入；与 [dataTypeNames] 配对使用。[NullSafetyChecker] 在 MemberAccess 上
     * 优先查同文件再查跨文件 [crossFileTypes]，命中即放行，未命中触发 E669。
     *
     * 仅承载字段名（不带类型）；字段类型反查目前留给 TypeInferencer 通过
     * crossFileTypes 解决，本表只服务"字段名是否存在"的校验目标。
     */
    val localDataFields: MutableMap<String, MutableSet<String>> = mutableMapOf()

    /**
     * M10 Stage 4：跨文件类型表（项目级，从 IDE stub index 收集）。
     *
     * 在 [TypeConstraintChecker.check] 入口由调用方注入，整次 check 期间不变；
     * [reset] / [resetForNewFun] 都不重置——它属于"项目级编辑器上下文"，与 fun /
     * 作用域生命周期解耦。默认 [ProjectTypeTable.EMPTY]，单文件 / 旧调用路径无副作用。
     */
    var crossFileTypes: ProjectTypeTable = ProjectTypeTable.EMPTY

    /**
     * M10 Stage 6 / 数据流分析：当前作用域内被窄化的 LocalRef → 窄化后类型。
     *
     * 由 [TypeConstraintChecker.walkStmt] 在进入 IfStmt then 分支前 push、
     * 退出后 pop 维护。优先级高于 [declaredVarTypes] / [inferredVarTypes]，
     * [currentVarEnv] 合并时窄化表覆盖原表。
     *
     * 第一版仅处理：
     * - `if (x is TypeName)` 的 then 分支：x 窄化为 TypeName 对应 IrType
     * - `if (x != null)` 的 then 分支：x 窄化为 unwrapNullable(原类型)
     *
     * else 分支不做反向窄化（M11+ 再说）；break / continue / return 跨块的 CFG
     * 边界保留为简化版——则保持 INFO 级 E666。
     *
     * break / continue / return 跨块的 CFG 边界保留为简化版（plan §9.6）——这些情形保持 INFO 级 E666，留 M11+ 扩展。
     */
    val narrowedTypes: MutableMap<String, IrType> = mutableMapOf()

    /** 全量重置：每次 [TypeConstraintChecker.check] 入口调用一次。 */
    fun reset() {
        dataTypeNames.clear()
        stateFieldTypes.clear()
        stateFieldIrTypes.clear()
        declaredVarTypes.clear()
        inferredVarTypes.clear()
        currentFunParams.clear()
        currentForLoopVars.clear()
        localDataFields.clear()
        narrowedTypes.clear()
        // 注意：crossFileTypes 不重置——它是项目级输入，整次 check 期间稳定不变
    }

    /** 进入新 fun / action 体时调用：与原 check 中每个 fun 入口的清理一致。 */
    fun resetForNewFun() {
        declaredVarTypes.clear()
        inferredVarTypes.clear()
        currentFunParams.clear()
        // 窄化是块作用域而非 fun 作用域；进入新 fun 一律清空，避免上一个 fun 残留污染
        narrowedTypes.clear()
    }

    /**
     * 在 [name] 局部窄化为 [narrowedType] 的作用域内执行 [block]。
     *
     * 进入前 push 记录原值（可能为 null），退出后 pop 恢复——保证嵌套窄化与
     * 异常路径都能正确恢复栈。
     */
    inline fun withNarrowedType(name: String, narrowedType: IrType, block: () -> Unit) {
        val hadPrevious = narrowedTypes.containsKey(name)
        val previous = narrowedTypes[name]
        narrowedTypes[name] = narrowedType
        try {
            block()
        } finally {
            if (hadPrevious) narrowedTypes[name] = previous!! else narrowedTypes.remove(name)
        }
    }

    /**
     * 在嵌套作用域内执行 [block]：保存 / 清空 [declaredVarTypes]、[inferredVarTypes]、
     * [currentFunParams] 与 [narrowedTypes]，退出后恢复，确保内层 var 名 / 形参名 / 数据流
     * 窄化记录不污染外层。
     *
     * 用 try / finally 守住"异常时也恢复外层表"的不变量；调用方不应在嵌套作用域内
     * 自行 clear / putAll，避免与本方法争用同一份状态。
     *
     * **M10 Stage 6d**：除了 declared / inferred / params 三表，还把 [narrowedTypes] 一并
     * save/clear/restore。嵌套 fun 是独立作用域，外层 `if (x is T)` 的窄化不应渗透到内层
     * 同名 var；否则 NullSafetyChecker 在内层 MemberAccess 上会按外层窄化结果误判，让
     * 真实可空类型上的 `.field` 漏报 E666。
     *
     * @param nestedParams 内层 fun 的形参名列表（嵌套 fun 进入新作用域前替换）
     */
    inline fun withScopedDeclaredVars(
        nestedParams: List<String> = emptyList(),
        block: () -> Unit,
    ) {
        val savedDeclared = HashMap(declaredVarTypes)
        val savedInferred = HashMap(inferredVarTypes)
        val savedParams = HashSet(currentFunParams)
        // M10 Stage 6d：嵌套 fun 是独立作用域，外层 `if (x is T)` 窄化不应渗透进内层；
        // 与 declared / inferred / params 表对称地 save/clear/restore narrowedTypes，
        // 避免内层同名 var 的真实类型被外层窄化静默覆盖。
        val savedNarrowed = HashMap(narrowedTypes)
        declaredVarTypes.clear()
        inferredVarTypes.clear()
        currentFunParams.clear()
        narrowedTypes.clear()
        currentFunParams += nestedParams
        try {
            block()
        } finally {
            declaredVarTypes.clear()
            declaredVarTypes.putAll(savedDeclared)
            inferredVarTypes.clear()
            inferredVarTypes.putAll(savedInferred)
            currentFunParams.clear()
            currentFunParams += savedParams
            narrowedTypes.clear()
            narrowedTypes.putAll(savedNarrowed)
        }
    }

    /** for 循环迭代变量入栈 / 出栈：嵌套循环用同一个 set，依赖入栈前不存在的不变量。 */
    inline fun withForLoopVar(varName: String, block: () -> Unit) {
        val added = currentForLoopVars.add(varName)
        try {
            block()
        } finally {
            // 仅当本层确实新增过才删除，避免外层同名变量被误删
            if (added) currentForLoopVars.remove(varName)
        }
    }

    /**
     * 把当前作用域内已知的 var 类型合并成 [top.wcpe.allincore.dsl.compiler.type.TypeInferencer.inferExpr]
     * 用的 env。
     *
     * 优先级：[narrowedTypes] > [declaredVarTypes] > [inferredVarTypes]。
     * 已标注类型优先（同名 var 不会同时进两表，但理论上 declaredVarTypes 更精准）；
     * 数据流窄化（M10 Stage 6 / `is` / `!= null`）覆盖一切，让 then 分支体内的
     * MemberAccess / Elvis 等都能见到窄化后的类型。
     */
    fun currentVarEnv(): Map<String, IrType> {
        if (declaredVarTypes.isEmpty() && inferredVarTypes.isEmpty() && narrowedTypes.isEmpty()) {
            return emptyMap()
        }
        val env = HashMap<String, IrType>(
            declaredVarTypes.size + inferredVarTypes.size + narrowedTypes.size,
        )
        env.putAll(inferredVarTypes)
        env.putAll(declaredVarTypes)
        env.putAll(narrowedTypes)
        return env
    }
}
